import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { QuantitativeMetadataBarChart } from "../NumberMetadataBreakdown/QuantitativeMetadataBarChart";
import { OverallSentimentDetailsModal } from "../SentimentWidget/OverallSentimentDetailModal";
import LessThanOnePercentModal from "../NumberMetadataBreakdown/LessThanOnePercentModal";

const NpsBreakdown = (props) => {
    const { selectedFieldDriverName,driverInterval, id, filters } = props;
    const [chartData, setChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedSentiment, setSelectedSentiment] = useState(null);
    const [topConceptsBySentiment, setTopConceptsBySentiment] = useState({});
    const [additionalFilter, setAdditionalFilter] = useState([]);
    const [showLessThanOnePercentModal, setShowLessThanOnePercentModal] =
        useState(false);
    const [lessThanOnePercentValues, setLessThanOnePercentValues] = useState(
        [],
    );

    const [getVolume] = useGetConceptsFiltersMutation();
    const conceptFilter = useMemo(
        () =>
            JSON.stringify(
                getConceptFilter({ ...props, selectedPageSize: 100 }),
            ),
        [props],
    );
    const getConceptData = useCallback(async () => {
        try {
            setIsLoading(true);

            const { data: dataConcepts } = await getVolume({
                id,
                concept_selector: JSON.parse(conceptFilter),
                filter: filters,
                breakdowns: [{ name: selectedFieldDriverName, interval: driverInterval }],
            });

            const breakdownData = dataConcepts.breakdowns[0].buckets;
            const totalCount = dataConcepts.filter_count;
            const pieData = breakdownData.map((concept) => ({
                name: concept.label,
                value: concept.filter_count / totalCount,
                count: concept.filter_count,
            }));
            const lessThanOnePercent = pieData.filter(
                (item) => item.value < 0.01,
            );
            setLessThanOnePercentValues(lessThanOnePercent);

            const conceptsByLabel = processConceptsByLabel(breakdownData);
            setTopConceptsBySentiment(conceptsByLabel);
            setChartData(pieData);
            setIsLoading(false);
        } catch (e) {
            console.log(e);
            setIsLoading(false);
        }
    }, [
        filters,
        selectedFieldDriverName,
        driverInterval
    ]);

    useEffect(() => {
        setIsLoading(true);
        const debouncedGetData = debounce(getConceptData, 1000);

        debouncedGetData();

        return () => {
            debouncedGetData.cancel();
        };
    }, [getConceptData]);

    const handlePieClick = (entry) => {
        if (entry.name === "< 1%") {
            setShowLessThanOnePercentModal(true);
            return;
        }
        setShowLessThanOnePercentModal(false);
        setSelectedSentiment(entry.name.toString().toLowerCase());
        setAdditionalFilter([
            { name: selectedFieldDriverName, maximum: entry.name, minimum: entry.name },
        ]);
    };


    if (isLoading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }


    return (
        <div>
            <div>
                <QuantitativeMetadataBarChart
                    nps={true}
                    chartData={chartData}
                    handlePieClick={handlePieClick}
                />

                <OverallSentimentDetailsModal
                    additionalFilter={additionalFilter}
                    selectedSentiment={selectedSentiment}
                    setSelectedSentiment={setSelectedSentiment}
                    topConceptsBySentiment={topConceptsBySentiment}
                />
                <LessThanOnePercentModal
                    open={showLessThanOnePercentModal}
                    values={lessThanOnePercentValues}
                    onClickValue={handlePieClick}
                    onClose={() => setShowLessThanOnePercentModal(false)}
                />
            </div>
        </div>
    );
};

export default NpsBreakdown;


const processConceptsByLabel = (breakdownData) => {
    const conceptsByLabel = {};

    breakdownData.forEach((concept) => {
        conceptsByLabel[concept.label] = concept.match_counts.map((match_count) => ({
            name: match_count.name,
            count: ((match_count.match_count * 100) / concept.filter_count).toFixed(2),
        }));
    });

    Object.keys(conceptsByLabel).forEach((label) => {
        conceptsByLabel[label] = conceptsByLabel[label]
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
            .map((concept) => ({
                ...concept,
                count: `${concept.count}%`,
            }));
    });

    return conceptsByLabel;
};
